"use client";
import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import CustomButton from "../pages/CustomButton";
const findId = (list, value, ...fields) => {
  if (!value || !Array.isArray(list) || list.length === 0) return "";
  const needle = String(value).trim().toLowerCase();
  const found = list.find((item) =>
    fields.some(
      (f) => item[f] != null && String(item[f]).trim().toLowerCase() === needle
    )
  );
  if (!found) return "";
  return (
    found.id ??
    found.city_id ??
    found.area_id ??
    found.route_id ??
    found.value ??
    ""
  );
};

const normalizePhone = (s) => (s ? String(s).replace(/\D/g, "") : "");
const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

const getCategoryId = (cat) => {
  if (!cat) return null;
  const c = String(cat).trim().toLowerCase();
  if (c === "general") return 1;
  if (c === "specialist") return 2;
  return null;
};

/* ─────────────────────────────────────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────────────────────────────────────── */
const EditDoctor = () => {
  const location = useLocation();
  const navigate = useNavigate();

  /* ── URL / nav-state params ─────────────────────────────────────────── */
  const doctorCode = new URLSearchParams(location.search).get("doctor_code");
  const fromDuplicate = location.state?.fromEdit === true;
  const employeeId = location.state?.employeeId || "";
  const passedCities = location.state?.assignedCities || [];
  const previousState = location.state || {};
  const previousFilters = previousState.filters || {};
  const selectedEmployeeName = previousState.selectedEmployeeName || "";

  /* ── Form state ─────────────────────────────────────────────────────── */
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    category: "General",
    speciality_id: "",
    qualification_id: "",
    contact_person: "",
    country: "India",
    state_id: "",
    city_id: "",
    area_id: "",
    route_id: "",
    phone: "",
    mobile: "",
    email: "",
    address: "",
    clinic_address: "",
    residence_address: "",
    pin: "",
    address_map: "",
    registration_number: "",
    frequency: "",
    annual_potential: "",
    number_of_patients: "",
    clinic_number: "",
    residence_number: "",
    svl: "",
    is_active: true,
  });

  /* ── Dropdown lists ─────────────────────────────────────────────────── */
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [areas, setAreas] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [specialities, setSpecialities] = useState([]);
  const [qualifications, setQualifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const CATEGORIES = ["General", "Specialist"];

  /* ── Shared fetch helper ─────────────────────────────────────────────
     Returns [] on error so callers don't need try/catch for every call.
  ─────────────────────────────────────────────────────────────────────── */
  const get = useCallback(
    async (url) => {
      try {
        const res = await axios.get(`${BASE_URL}${url}`, { headers: getAuthHeaders() });
        return res.data || [];
      } catch (err) {
        console.error(`GET ${url} failed:`, err.response?.data || err.message);
        return [];
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );


  useEffect(() => {
    if (!doctorCode) {
      alert("Invalid doctor code");
      navigate(-1);
      return;
    }

    const fetchAll = async () => {
      setLoading(true);
      try {
        /* ── 1. Parallel static fetches ──────────────────────────────── */
        const [statesList, doctorData, specialitiesList, qualificationsList] =
          await Promise.all([
            get("/api/states"),
            get(`/api/doctors/full-details/${doctorCode}`),
            get("/api/specialities/"),
            get("/api/qualifications/"),
          ]);

        setStates(statesList);
        setSpecialities(specialitiesList);
        setQualifications(qualificationsList);

        if (!doctorData || Object.keys(doctorData).length === 0) {
          throw new Error("Doctor not found");
        }

        /* ── 2. Resolve state_id ─────────────────────────────────────── */
        const state_id =
          doctorData.state_id ||
          findId(statesList, doctorData.state, "name", "state_name");

        /* ── 3. Resolve speciality / qualification ids ───────────────── */
        const speciality_id =
          doctorData.speciality?.id ||
          doctorData.speciality_id ||
          findId(specialitiesList, doctorData.speciality, "name");

        const qualification_id =
          doctorData.qualification?.id ||
          doctorData.qualification_id ||
          findId(qualificationsList, doctorData.qualification, "name");

        /* ── 4. Fetch cities ─────────────────────────────────────────── */
        let citiesList = [];
        if (employeeId) {
          citiesList = await get(`/api/assigned-cities/${employeeId}`);
        } else if (passedCities.length > 0) {
          citiesList = passedCities;
        } else if (state_id) {
          citiesList = await get(`/api/cities/${state_id}`);
        }
        setCities(citiesList);

        /* ── 5. Resolve city_id ──────────────────────────────────────── */
        const city_id =
          doctorData.city_id ||
          findId(citiesList, doctorData.city,
            "city_name", "name", "cityName", "label");

        /* ── 6. Fetch areas by city_id ───────────────────────────────── */
        let areasList = [];
        if (city_id) {
          areasList = await get(`/api/areas/${city_id}`);
        }
        setAreas(areasList);

        /* ── 7. Resolve area_id ──────────────────────────────────────── */
        const area_id =
          doctorData.area_id ||
          findId(areasList, doctorData.area,
            "area_name", "name", "areaName", "label");

        /* ── 8. Fetch routes ─────────────────────────────────────────── */
        let routesList = [];
        if (employeeId && city_id) {
          routesList = await get(`/api/routes/${employeeId}/?city_id=${city_id}`);
        } else if (area_id) {
          routesList = await get(`/api/routes/${area_id}`);
        }
        setRoutes(routesList);

        /* ── 9. Resolve route_id ─────────────────────────────────────── */
        const route_id =
          doctorData.route_id ||
          findId(routesList, doctorData.route,
            "route_name", "name", "routeName", "label");

        /* ── 10. Debug log — remove after confirming all IDs resolve ─── */
        if (
          process.env.NODE_ENV === "development" &&
          (!city_id || !area_id || !route_id)
        ) {
          console.warn("⚠ EditDoctor — unresolved IDs", {
            state: { raw: doctorData.state, id: state_id },
            city: {
              raw: doctorData.city, id: city_id,
              sample: citiesList.slice(0, 3)
            },
            area: {
              raw: doctorData.area, id: area_id,
              sample: areasList.slice(0, 3)
            },
            route: {
              raw: doctorData.route, id: route_id,
              sample: routesList.slice(0, 3)
            },
          });
        }

        /* ── 11. Commit form data ────────────────────────────────────── */
        setFormData((prev) => ({
          ...prev,
          first_name: doctorData.first_name || "",
          last_name: doctorData.last_name || "",
          category:
            doctorData.category ||
            (doctorData.category_id === 1 ? "General"
              : doctorData.category_id === 2 ? "Specialist"
                : "General"),
          speciality_id,
          qualification_id,
          contact_person: doctorData.employee_name || doctorData.contact_person || "",
          country: doctorData.country || "India",
          state_id,
          city_id,
          area_id,
          route_id,
          phone: doctorData.phone || "",
          mobile: doctorData.mobile || "",
          email: doctorData.email || "",
          address: doctorData.address || "",
          clinic_address: doctorData.clinic_address || "",
          residence_address: doctorData.residence_address || "",
          pin: String(doctorData.pin ?? doctorData.pincode ?? doctorData.zip ?? ""),
          address_map: doctorData.address_map || "",
          registration_number: doctorData.registration_number || "",
          frequency: doctorData.frequency || "",
          annual_potential: doctorData.annual_potential || "",
          number_of_patients: doctorData.number_of_patients || "",
          clinic_number: doctorData.clinic_number || "",
          residence_number: doctorData.residence_number || "",
          svl: String(doctorData.svl ?? doctorData.svl_number ?? doctorData.svlNo ?? ""),
          is_active:
            typeof doctorData.is_active === "boolean" ? doctorData.is_active : true,
        }));
      } catch (error) {
        console.error("EditDoctor load error:", error);
        alert("Failed to load doctor details. Returning…");
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorCode]);

  /* ═══════════════════════════════════════════════════════════════════════
     INPUT HANDLERS
  ═══════════════════════════════════════════════════════════════════════ */
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  /* ── State change ─────────────────────────────────────────────────── */
  const handleStateChange = async (e) => {
    const stateId = e.target.value;
    setFormData((prev) => ({
      ...prev,
      state_id: stateId,
      city_id: "", area_id: "", route_id: "",
    }));
    setCities([]); setAreas([]); setRoutes([]);

    if (employeeId) {
      setCities(await get(`/api/assigned-cities/${employeeId}`));
    } else if (stateId) {
      setCities(await get(`/api/cities/${stateId}`));
    }
  };

  /* ── City change ──────────────────────────────────────────────────── */
  const handleCityChange = async (e) => {
    const cityId = e.target.value;
    setFormData((prev) => ({
      ...prev,
      city_id: cityId,
      area_id: "", route_id: "",
    }));
    setAreas([]); setRoutes([]);
    if (!cityId) return;

    const [areasList, routesList] = await Promise.all([
      get(`/api/areas/${cityId}`),
      employeeId
        ? get(`/api/routes/${employeeId}/?city_id=${cityId}`)
        : Promise.resolve([]),
    ]);
    setAreas(areasList);
    setRoutes(routesList);
  };

  /* ── Area change ──────────────────────────────────────────────────── */
  const handleAreaChange = async (e) => {
    const areaId = e.target.value;
    setFormData((prev) => ({ ...prev, area_id: areaId, route_id: "" }));

    // Only re-fetch routes if the list is empty (already loaded at city level)
    if (routes.length > 0) return;

    if (employeeId && formData.city_id) {
      setRoutes(await get(`/api/routes/${employeeId}/?city_id=${formData.city_id}`));
    } else if (areaId) {
      setRoutes(await get(`/api/routes/${areaId}`));
    }
  };

  /* ═══════════════════════════════════════════════════════════════════════
     NAVIGATION
  ═══════════════════════════════════════════════════════════════════════ */
  const handleReturn = () => {
    if (fromDuplicate) {
      navigate("/ViewChemist", {
        state: {
          fromEdit: true,
          shouldRefresh: true,
          filters: previousFilters,
          selectedEmployeeName,
        },
      });
    } else {
      navigate(-1);
    }
  };

  /* ═══════════════════════════════════════════════════════════════════════
     UPDATE / SUBMIT
  ═══════════════════════════════════════════════════════════════════════ */
  const handleUpdate = async () => {
    const mobile = normalizePhone(formData.mobile);

    // ── Mandatory field guards ────────────────────────────────────────
    if (!formData.first_name?.trim()) { alert("First name is required"); return; }
    if (!getCategoryId(formData.category)) { alert("Category is required"); return; }
    if (!formData.city_id) { alert("City is required"); return; }
    if (!formData.area_id) { alert("Area is required"); return; }
    if (!formData.route_id) { alert("Route is required"); return; }
    if (!mobile || mobile.length < 10) { alert("Mobile must have at least 10 digits"); return; }

    const payload = {
      first_name: formData.first_name?.trim() || "",
      sex: formData.sex || "Male",

      category_id: getCategoryId(formData.category),

      city_id: Number(formData.city_id),

      area_id: Number(formData.area_id),

      route_id: Number(formData.route_id),

      mobile: normalizePhone(formData.mobile),

      speciality_id: formData.speciality_id
        ? Number(formData.speciality_id)
        : null,

      contact_person: formData.contact_person || "",

      country: formData.country || "India",

      state_id: formData.state_id
        ? Number(formData.state_id)
        : null,

      is_active: Boolean(formData.is_active),
    };

    try {
      await axios.put(`${BASE_URL}/api/doctors/${doctorCode}`, payload, {
        headers: getAuthHeaders(),
      });
      alert("Updated successfully!");
      handleReturn();
    } catch (error) {
      console.error("Update error:", error.response?.data || error.message);
      alert(`Update failed: ${error.response?.data?.detail || error.message}`);
    }
  };

  /* ═══════════════════════════════════════════════════════════════════════
     SHARED CLASS STRINGS
  ═══════════════════════════════════════════════════════════════════════ */
  const ROW_GRID = "grid grid-cols-1 md:grid-cols-2 gap-4";
  const FIELD = "flex items-center gap-3";
  const LBL = "font-medium text-gray-700 whitespace-nowrap w-[150px] shrink-0 text-sm";
  const LBL_REQ = "font-medium text-orange-600 whitespace-nowrap w-[150px] shrink-0 text-sm after:content-['_*']";
  const LBL_WIDE = "font-medium text-gray-700 whitespace-nowrap w-[190px] shrink-0 text-sm";
  const INPUT = "flex-1 min-w-0 border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition";
  const SELECT = "flex-1 min-w-0 border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white disabled:bg-gray-100 disabled:cursor-not-allowed";
  const LEGEND = "bg-green-50 border border-green-300 px-4 py-1.5 text-sm font-semibold text-green-800 rounded-sm";
  const SECTION = "border border-gray-200 rounded-md mb-5 shadow-sm";

  /* ─────────────────────────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="flex justify-center items-center h-96 text-gray-500">
        <svg className="animate-spin h-5 w-5 mr-3 text-blue-500" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
        </svg>
        Loading doctor details…
      </div>
    );
  }

  /* ═══════════════════════════════════════════════════════════════════════
     RENDER
  ═══════════════════════════════════════════════════════════════════════ */
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 py-4 px-3">
      <fieldset className="border-2 border-gray-300 rounded-lg p-5 bg-white shadow">
        <legend className="text-base font-semibold px-2 text-gray-800">
          Edit Doctor —{" "}
          <span className="text-blue-600 font-bold">{doctorCode}</span>
        </legend>

        <p className="text-right text-orange-500 text-xs mb-4">
          Fields marked * are mandatory.
        </p>

        {/* ══════════════════════════════════════════════════════════════
            BASIC DETAILS
        ══════════════════════════════════════════════════════════════ */}
        <fieldset className={SECTION}>
          <legend className={LEGEND}>Basic Details</legend>
          <div className="p-4 space-y-3">

            {/* First Name | Last Name */}
            <div className={ROW_GRID}>
              <div className={FIELD}>
                <label className={LBL_REQ}>First Name</label>
                <input
                  type="text" name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  className={INPUT}
                  placeholder="Enter first name"
                />
              </div>
              <div className={FIELD}>
                <label className={LBL}>Last Name</label>
                <input
                  type="text" name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  className={INPUT}
                  placeholder="Enter last name"
                />
              </div>
            </div>

            {/* Category | Speciality */}
            <div className={ROW_GRID}>
              <div className={FIELD}>
                <label className={LBL_REQ}>Category</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className={SELECT}
                >
                  <option value="">Select</option>
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className={FIELD}>
                <label className={LBL}>Speciality</label>
                <select
                  name="speciality_id"
                  value={formData.speciality_id}
                  onChange={handleInputChange}
                  className={SELECT}
                >
                  <option value="">Select</option>
                  {specialities.map((sp) => (
                    <option key={sp.id} value={sp.id}>{sp.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Qualification | Contact Person */}
            <div className={ROW_GRID}>
              <div className={FIELD}>
                <label className={LBL}>Qualification</label>
                <select
                  name="qualification_id"
                  value={formData.qualification_id}
                  onChange={handleInputChange}
                  className={SELECT}
                >
                  <option value="">Select</option>
                  {qualifications.map((q) => (
                    <option key={q.id} value={q.id}>{q.name}</option>
                  ))}
                </select>
              </div>
              <div className={FIELD}>
                <label className={LBL}>Contact Person</label>
                <input
                  type="text" name="contact_person"
                  value={formData.contact_person}
                  onChange={handleInputChange}
                  className={INPUT}
                  placeholder="Enter contact person"
                />
              </div>
            </div>

          </div>
        </fieldset>

        {/* ══════════════════════════════════════════════════════════════
            LOCATION INFORMATION
        ══════════════════════════════════════════════════════════════ */}
        <fieldset className={SECTION}>
          <legend className={LEGEND}>Location Information</legend>
          <div className="p-4 space-y-3">

            {/* Country | State */}
            <div className={ROW_GRID}>
              <div className={FIELD}>
                <label className={LBL_REQ}>Country</label>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  className={SELECT}
                >
                  <option value="India">India</option>
                </select>
              </div>
              <div className={FIELD}>
                <label className={LBL}>State</label>
                <select
                  name="state_id"
                  value={formData.state_id}
                  onChange={handleStateChange}
                  className={SELECT}
                >
                  <option value="">Select</option>
                  {states.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* City | Area
                FIX: disabled only when the list is genuinely empty,
                     not tied to state_id being present.              */}
            <div className={ROW_GRID}>
              <div className={FIELD}>
                <label className={LBL_REQ}>City</label>
                <select
                  name="city_id"
                  value={formData.city_id}
                  onChange={handleCityChange}
                  disabled={cities.length === 0}          // ✅ FIX #1
                  className={SELECT}
                >
                  <option value="">
                    {cities.length === 0 ? "No cities available" : "Select city"}
                  </option>
                  {cities.map((c) => (
                    <option key={c.id ?? c.city_id} value={c.id ?? c.city_id}>
                      {c.city_name ?? c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className={FIELD}>
                <label className={LBL_REQ}>Area</label>
                <select
                  name="area_id"
                  value={formData.area_id}
                  onChange={handleAreaChange}
                  disabled={areas.length === 0}            // ✅ FIX #2
                  className={SELECT}
                >
                  <option value="">
                    {!formData.city_id
                      ? "Select city first"
                      : areas.length === 0
                        ? "No areas available"
                        : "Select area"}
                  </option>
                  {areas.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.area_name ?? a.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Route | PIN
                FIX: disabled only when routes list is empty AND city
                     is not yet selected — not tied to area_id.       */}
            <div className={ROW_GRID}>
              <div className={FIELD}>
                <label className={LBL_REQ}>Route</label>
                <select
                  name="route_id"
                  value={formData.route_id}
                  onChange={handleInputChange}
                  disabled={routes.length === 0}           // ✅ FIX #3
                  className={SELECT}
                >
                  <option value="">
                    {!formData.city_id
                      ? "Select city first"
                      : routes.length === 0
                        ? "No routes available"
                        : "Select route"}
                  </option>
                  {routes.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.route_name ?? r.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className={FIELD}>
                <label className={LBL}>PIN Code</label>
                <input
                  type="text" name="pin"
                  value={formData.pin}
                  onChange={handleInputChange}
                  className={INPUT}
                  placeholder="Enter PIN code"
                  maxLength={6}
                />
              </div>
            </div>

            {/* Status */}
            <div className={FIELD}>
              <label className={LBL}>Status</label>
              <div className="flex gap-6">
                {[
                  { label: "Active", value: true },
                  { label: "Inactive", value: false },
                ].map(({ label, value }) => (
                  <label key={label} className="flex items-center gap-2 cursor-pointer text-sm">
                    <input
                      type="radio" name="is_active"
                      checked={formData.is_active === value}
                      onChange={() =>
                        setFormData((prev) => ({ ...prev, is_active: value }))
                      }
                      className="w-4 h-4 accent-blue-600"
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>

          </div>
        </fieldset>

        {/* ══════════════════════════════════════════════════════════════
            CONTACT INFORMATION
        ══════════════════════════════════════════════════════════════ */}
        <fieldset className={SECTION}>
          <legend className={LEGEND}>Contact Information</legend>
          <div className="p-4 space-y-3">

            {/* Mobile | Phone */}
            <div className={ROW_GRID}>
              <div className={FIELD}>
                <label className={LBL_REQ}>Mobile Number</label>
                <input
                  type="text" name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  className={INPUT}
                  placeholder="10-digit mobile"
                  maxLength={10}
                />
              </div>
              <div className={FIELD}>
                <label className={LBL}>Phone Number</label>
                <input
                  type="text" name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={INPUT}
                  placeholder="Landline number"
                />
              </div>
            </div>

            {/* Email | Clinic Number */}
            <div className={ROW_GRID}>
              <div className={FIELD}>
                <label className={LBL}>Email</label>
                <input
                  type="email" name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={INPUT}
                  placeholder="email@example.com"
                />
              </div>
              <div className={FIELD}>
                <label className={LBL}>Clinic Number</label>
                <input
                  type="text" name="clinic_number"
                  value={formData.clinic_number}
                  onChange={handleInputChange}
                  className={INPUT}
                  placeholder="Clinic contact number"
                />
              </div>
            </div>

            {/* Residence Number | SVL */}
            <div className={ROW_GRID}>
              <div className={FIELD}>
                <label className={LBL}>Residence Number</label>
                <input
                  type="text" name="residence_number"
                  value={formData.residence_number}
                  onChange={handleInputChange}
                  className={INPUT}
                  placeholder="Residence contact number"
                />
              </div>
              <div className={FIELD}>
                <label className={LBL}>SVL</label>
                <input
                  type="text" name="svl"
                  value={formData.svl}
                  onChange={handleInputChange}
                  className={INPUT}
                  placeholder="SVL number"
                />
              </div>
            </div>

          </div>
        </fieldset>

        {/* ══════════════════════════════════════════════════════════════
            PROFESSIONAL + ADDRESS (side by side on large screens)
        ══════════════════════════════════════════════════════════════ */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">

          {/* Professional Information */}
          <fieldset className="border border-gray-200 rounded-md shadow-sm">
            <legend className={LEGEND}>Professional Information</legend>
            <div className="p-4 space-y-3">
              {[
                ["Registration No.", "registration_number", "Reg. number"],
                ["Frequency", "frequency", "Visit frequency"],
                ["Annual Potential", "annual_potential", "Annual potential value"],
                ["No. of Patients", "number_of_patients", "Patient count"],
              ].map(([label, name, placeholder]) => (
                <div key={name} className={FIELD}>
                  <label className={LBL_WIDE}>{label}</label>
                  <input
                    type="text" name={name}
                    value={formData[name]}
                    onChange={handleInputChange}
                    className={INPUT}
                    placeholder={placeholder}
                  />
                </div>
              ))}
            </div>
          </fieldset>

          {/* Address Information */}
          <fieldset className="border border-gray-200 rounded-md shadow-sm">
            <legend className={LEGEND}>Address Information</legend>
            <div className="p-4 space-y-3">
              {[
                ["Clinic Address", "clinic_address", "Clinic full address"],
                ["Residence Address", "residence_address", "Residence full address"],
              ].map(([label, name, placeholder]) => (
                <div key={name} className={FIELD}>
                  <label className={LBL_WIDE}>{label}</label>
                  <textarea
                    name={name}
                    value={formData[name]}
                    rows={3}
                    onChange={handleInputChange}
                    className={`${INPUT} resize-none`}
                    placeholder={placeholder}
                  />
                </div>
              ))}
            </div>
          </fieldset>

        </div>

        {/* ══════════════════════════════════════════════════════════════
            ACTION BUTTONS
        ══════════════════════════════════════════════════════════════ */}
        <div className="flex justify-center gap-4 pt-2">
          <CustomButton
            onClick={handleUpdate}
            variant="save"
            className="text-sm font-medium px-8"
          >
            UPDATE
          </CustomButton>
          <CustomButton
            onClick={handleReturn}
            variant="cancel"
            className="text-sm font-medium px-8"
          >
            RETURN
          </CustomButton>
        </div>

      </fieldset>
    </div>
  );
};

export default EditDoctor;